<template>
  <div v-if="loggedIn" class="px-4 !mb-4">
    <SelectSingleList
      v-model="focusedChapterId"
      :label="t(key('chapter_selector.label'))"
      @update:model-value="() => (userInteracted = true)"
      :options="chapterOptions"
      :disabled="isPending"
    />
  </div>
  <NamiLoading v-if="isPending" size="small" />
  <div v-else-if="loggedIn" class="px-2 divide-y dark:divide-neutral-700 !mt-0">
    <NovelBookmarkCard
      class="rounded-none cursor-pointer"
      v-for="bm in orderedHighlights"
      :bookmark="bm"
      no-name
      @click="emit('go', bm.relationships.find(isNovelParagraph)!.id)"
      @edit="emit('edit', [bm])"
      @delete="emit('delete', [bm])"
    />
    <NamiButton
      v-if="hasNextPage"
      button-type="primary"
      @button-click="fetchNextPage"
      block
      small
      text
    >
      {{ t(key("load_more")) }}
    </NamiButton>
    <div class="px-2 text-center" v-else-if="!orderedHighlights.length">
      {{ t(key("no_results")) }}
    </div>
  </div>
  <div v-else>
    {{ t(key("login_required")) }}
  </div>
</template>

<script setup lang="ts">
import {
  isNovelChapter,
  isNovelParagraph,
  type NovelBookmarkEntity,
} from "~/src/api";
import { useQueryClient } from "@tanstack/vue-query";
import { useNovelHighlightManager } from "~/src/novel/reader/highlight";
import { natSortNulls } from "~/src/reader/utils";
import type { Option } from "~/types/input";
import type { getChapterSequenceForLang } from "~/src/reader/generic";

const { t } = useI18n();
const key = useI18nKey(
  "components.novel.drawer.bookmarks.novelDrawerBookmarksHighlights",
);
const loggedIn = useIsLoggedIn();

const { chapterId, titleId, paragraphOrder, chapterSequence } = defineProps<{
  titleId: string;
  chapterId: string;
  paragraphOrder?: string[];
  chapterSequence: ReturnType<typeof getChapterSequenceForLang>;
}>();

const emit = defineEmits<{
  (e: "go", paragraphId: string): void;
  (e: "edit", bookmark: NovelBookmarkEntity[]): void;
  (e: "delete", bookmark: NovelBookmarkEntity[]): void;
}>();

const queryClient = useQueryClient();
const { highlightQuery } = useNovelHighlightManager(
  queryClient,
  () => titleId,
  () => chapterId,
);
const { hasNextPage, fetchNextPage } = highlightQuery;

const { data, isPending } = highlightQuery;

const focusedChapterId = ref<string | undefined>(chapterId);
const userInteracted = ref(false);

const orderedHighlights = computed(() => {
  if (!data.value) return [];

  const clone = Array.from(data.value.data);

  return clone.sort((a, b) => {
    const bmACh = a.relationships.find(isNovelChapter);
    const bmBCh = b.relationships.find(isNovelChapter);

    const chapterOrder = -natSortNulls(
      bmACh?.attributes?.chapter ?? null,
      bmBCh?.attributes?.chapter ?? null,
    );
    const volumeOrder = -natSortNulls(
      bmACh?.attributes?.volume ?? null,
      bmBCh?.attributes?.volume ?? null,
    );

    const bmAP = a.relationships.find(isNovelParagraph);
    const bmBP = b.relationships.find(isNovelParagraph);

    const paraOrder =
      paragraphOrder && bmAP && bmBP
        ? paragraphOrder.indexOf(bmAP.id) - paragraphOrder.indexOf(bmBP.id)
        : undefined;

    const createdOrder = a.attributes.createdAt.localeCompare(
      b.attributes.createdAt,
    );

    return volumeOrder || chapterOrder || paraOrder || createdOrder;
  });
});

const chapterOptions = computed(() => {
  const mapped: Option<string | undefined>[] = chapterSequence
    .map((c) => ({
      text: createChapterName(c.chapter, c.volume, c.name, "short"),
      value: c.id,
    }))
    .reverse();
  mapped.unshift({
    text: t(key("chapter_selector.all_chapters")),
    value: undefined,
  });
  return mapped;
});
</script>
